/**
 * notifications/{id} — in-app notifications + the fan-out helpers that also
 * trigger email/SMS.
 *
 * Channels:
 *   - In-app: the authenticated `notify` Function validates the backing
 *     source and writes canonical cards with the Admin SDK. Client creates
 *     are denied by Firestore rules.
 *   - Email / SMS: the same Function reads each recipient's preferences and
 *     contact server-side and sends best-effort.
 *
 * Respecting prefs: in-app is always written (it is free + non-intrusive);
 * email/SMS are gated per-user by notifPrefs inside the function.
 */
import { useEffect, useState } from 'react';
import {
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import { functionHeaders } from './functionSecurity';
import {
  clearPendingBoardNotification,
  pendingBoardNotifications,
  rememberPendingBoardNotification,
  type PendingBoardNotification,
  type PendingBoardNotificationLane,
} from './boardNotificationRecovery';
import { useAuth } from './auth-context';
import {
  captureNotificationIdentity,
  notificationStillMatchesCapture,
  notificationMatchesTask,
  notificationMatchesThread,
  notificationMatchesClassPost,
  notificationMatchesDiscussion,
  notificationMatchesCompBoardPost,
  notificationMatchesBagGroup,
  notificationMatchesBigLittleGroup,
  notificationMatchesProductionUpdate,
  notificationMatchesSubRequest,
  notificationMatchesBulletin,
  notificationInCompBoardRow,
  compBoardRowThroughMs,
  type BulletinCardRef,
  type CompBoardRowBound,
  type CapturedNotificationIdentity,
  type CompletionNotificationRef,
} from './notificationCompletion';
import {
  nextNotificationHistoryLimit,
  visibleNotificationWindow,
} from './notificationWindow';
import { presentNotification } from './notificationPresentation';
import type { UserRole } from './users';
import type { TestSendResults } from './testSendReport';

/**
 * Text-message SIGN-UP is hidden for now (Bobbie, 2026-08-19, verbatim: "We
 * need to hide the text sign ups for now on the site"). Hiding, not removal:
 * stored SMS preference, saved phone number and send lanes stay intact.
 * Bobbie, 2026-09-13: the SMS option and phone-number input are hidden for
 * everyone while SMS is unavailable, including previously opted-in accounts.
 * Flip this to false to bring the sign-up back everywhere at once.
 */
export const SMS_SIGNUP_HIDDEN = true;

export type NotifKind =
  | 'message'
  | 'mention'
  | 'task'
  | 'bulletin'
  | 'newsletter'
  | 'board'
  | 'bag-group'
  | 'sub'
  | 'calendar'
  | 'comp'
  | 'attendance'
  | 'general';

export interface NotificationDoc {
  id: string;
  /** Recipient uid. */
  uid: string;
  senderUid: string;
  senderName: string;
  /** Server-authorized parent community thread that supplied senderName. */
  communityThreadId?: string;
  /** Backing ordinary message thread for a participant-to-participant alert. */
  threadId?: string;
  /** Backing task for newer task alerts. Older cards are matched by link. */
  taskId?: string;
  /** Backing parent coordination room and message. */
  bagGroupId?: string;
  bagMessageId?: string;
  /** Backing class post, plus reply when the alert is for a reply. */
  classPostId?: string;
  classReplyId?: string;
  /** Backing comp board post, plus reply when the alert is for a reply. */
  compBoardPostId?: string;
  compBoardReplyId?: string;
  /** The forum boards' own thread or post, plus the reply when the card is for one. */
  parentBoardThreadId?: string;
  parentBoardReplyId?: string;
  jrStaffBoardThreadId?: string;
  jrStaffBoardReplyId?: string;
  compParentsBoardPostId?: string;
  compParentsBoardReplyId?: string;
  /** Backing private Intern discussion, plus reply when applicable. */
  discussionId?: string;
  discussionReplyId?: string;
  /** One logical multi-class send. Used to collapse retries and class overlap. */
  broadcastId?: string;
  /** Backing sub request, plus one enrolled dancer for family delivery. */
  subRequestId?: string;
  studentId?: string;
  /** Backing class for a Recital/Comp Tracker update sent to the Directors. */
  productionOfferingId?: string;
  /** Backing studio announcement and the Show again round it was sent in.
   *  Stamped from 2026-09-25 on; older announcement cards carry neither. */
  bulletinId?: string;
  resendEpoch?: number;
  kind: NotifKind;
  title: string;
  body: string;
  /** In-app path to open when tapped. */
  link: string;
  read: boolean;
  /** One-way recipient archive. The card remains as the server's delivery marker. */
  dismissed?: boolean;
  createdAt: Timestamp | FieldValue;
  readAt?: Timestamp | FieldValue;
  dismissedAt?: Timestamp | FieldValue;
  /** Set by markNotificationUnread, kept forever after. While this stamp is
   *  newer than the linked source's inbox state, reconciliation leaves the
   *  card alone instead of flipping it back to read. Null in a snapshot means
   *  the recipient's own mark is still waiting on the server clock, which the
   *  reconciler treats as the newest thing that happened. */
  manuallyUnreadAt?: Timestamp | FieldValue | null;
  /** Raw Firestore identity captured before display-only copy migrations. */
  sourceIdentity?: CapturedNotificationIdentity;
}

export interface NotifyPayload {
  kind: NotifKind;
  title: string;
  body?: string;
  link?: string;
  /** Optional family-scoped dancer context. The server keeps it only when
   *  the recipient is currently linked to that dancer. */
  studentId?: string;
  /** Canonical id for a 'task' kind card, so a later capture-then-clear can
   *  match it exactly instead of only by link. Opaque display-only data on
   *  the server side; never used to grant read access (see
   *  notificationAuthority.mjs's unscoped lane). */
  taskId?: string;
  /** Canonical id for a 'bulletin' kind card, with the announcement's Show
   *  again round, so the recipient's own Got it, Dismiss or Done can find and
   *  clear this exact card (notificationAuthority.mjs's unscoped lane stamps
   *  both and derives the card id from them). Display-only, like taskId. */
  bulletinId?: string;
  resendEpoch?: number;
  /** The just-sent message's own id, for a 'message' or 'mention' kind card
   *  on the 'thread' lane (Codex round 5 on #801). The server bounds and
   *  stamps it onto the row so a later reader can bind to the EXACT send
   *  that rang someone, not merely "some row for this thread around this
   *  time" - the Jr Staff board mirror's durable-evidence check is the
   *  first consumer. Same opaque, display-only, no-read-access shape as
   *  taskId above; the server (notificationAuthority.mjs's 'thread' lane)
   *  only ever reads it for that one lane. */
  messageId?: string;
  /** A REMINDER about something already sent, such as the Command Center's
   *  Nudge. A Jr Staff's reminders go straight to her and are never copied to
   *  her parents (Bobbie, 2026-09-28, DECISIONS 64); notify.mjs reads this
   *  flag through lib/jrStaffReminders.mjs. It can only ever mean FEWER
   *  parent copies, never more. A new to-do is not a reminder: leave it off. */
  reminder?: boolean;
}

export interface NotifyOptions {
  server?: boolean;
  /**
   * A retry of an already-saved source uses a freshly minted credential. This
   * does not change who may notify whom: the function still re-reads and
   * authorizes the source. It only avoids retrying with an expired token.
   */
  forceRefreshToken?: boolean;
  communityThread?: {
    id: string;
    senderName: string;
  };
  thread?: { id: string };
  bagGroup?: { id: string; messageId: string };
  classPost?: { id: string; replyId?: string };
  compBoardPost?: { id: string; replyId?: string };
  /** A comp-date alert (Codex round 7 on #805, finding 1): the countdown
   *  event's own id, so the mute a member set on "the Comp Team board"
   *  applies to a date the same way it already applies to a post - the
   *  server keys isBoardNotificationMuted() off the SAME lane compBoardPost
   *  resolves to, never a second one, but re-reads the event itself
   *  (countdownEvents/{id}) rather than a post, since a date has no post
   *  doc and no author to re-prove against. Director/Manager only, same
   *  boundary canManageCountdowns() already draws - the client never sends
   *  a recipient list a family caller could not have resolved, so this
   *  carries no compTip-shaped "empty seed is OK" exception. */
  compBoardCountdown?: { id: string };
  /** A parent tip on the comp board. The server derives the audience from
   *  its own roll read - a family caller cannot resolve one. */
  compTip?: { id: string };
  /** A parent board thread, or a reply inside one. The server derives the
   *  audience: the whole board for a thread, the conversation for a reply. */
  parentBoard?: { threadId: string; replyId?: string };
  /** A comp parents board post, or a reply under one (packet R43, ruling
   *  9b). Same server-derived shape as the parent board: the client sends no
   *  recipients at all, because the board's yearly member list lives behind
   *  netlify/functions/compParentsBoard.mjs and no client can read it. The
   *  server resolves the whole board for a post AND a reply. */
  compParentsBoard?: { postId: string; replyId?: string };
  /** A Jr Staff board thread or reply. Same server-derived audience shape as
   *  the parent board: the whole board (staff by role plus the guardian
   *  roll) for a thread, the conversation for a reply.
   *
   *  `mirrorOfThreadId`/`mirrorOfMessageId` together name the exact
   *  messaging THREAD and MESSAGE this board post mirrors - a Jr Staff
   *  group message that also posted a copy here. Group chat itself notifies
   *  nobody on a plain reply by design (2026-08-08), so this board ping is
   *  most members' ONLY bell for a mirrored post. The client is never
   *  trusted with WHO the message send already notified, only WHICH
   *  message; the server re-reads that message's own stored fields itself
   *  (author, timestamp, ackRequired, mentions) and derives the exclusion
   *  from what the message lane can prove it already rang - never from
   *  thread membership alone. See resolveJrStaffBoardMirrorExclusion in
   *  notify.mjs for the full derivation. Both ids are required together;
   *  either one missing yields no exclusion at all. */
  jrStaffBoard?: {
    threadId: string;
    replyId?: string;
    mirrorOfThreadId?: string;
    mirrorOfMessageId?: string;
  };
  subRequest?: {
    id: string;
    /** One still-enrolled dancer linking each family recipient to the class. */
    recipientStudentIds?: Record<string, string>;
  };
  /**
   * NOT A LANE SELECTOR (Codex P1, PR #807 round 5). A reply's @handle
   * mentions are resolved on the client (lib/discussionAudience.ts's
   * parseReplyMentions) and sent through the ordinary unscoped kind:
   * 'mention' send - never notifyDiscussion, which canSendInternDiscussionNotification
   * refuses for a Jr Staff reply on purpose, since widening a reply to the
   * whole roll is a different, bigger send than "the people this reply
   * actually named." This field names WHICH discussion those recipientUids
   * were mentioned on, purely so the server can apply the one enforcement a
   * client-resolved list cannot carry: when that discussion is a Jr Staff
   * announcement, drop anyone the board's Manage-members overlay has since
   * removed, the same fresh overlay read notify.mjs already performs before
   * announcement delivery. Deliberately absent from authorityLane's lane
   * list server-side, so it never changes which lane a send is classified
   * into or which lane-specific gate applies - it only narrows recipientUids
   * before anything downstream, in-app write included, ever sees them.
   *
   * `replyId` (added PR #807 round 8, Codex P1-b) is REQUIRED alongside
   * discussionId now, not optional: the server no longer trusts
   * discussionId on its own (that was exactly the caller-optional gap
   * P1-b found - omit it, or point it at the wrong discussion, and the
   * removal fence never ran). The server instead loads the reply this
   * pair actually names, checks this caller wrote it, and derives the
   * discussion from where that reply really lives - see
   * loadVerifiedDiscussionReplyMention's own doc comment in
   * notificationAuthority.mjs. Both fields are always sent together by
   * this send's one caller (useReplyToDiscussion), which already has
   * both values in hand from the reply it just wrote. */
  discussionMention?: { discussionId: string; replyId: string };
  /**
   * The document behind a to-do assignment, a rehearsal schedule, a progress
   * note, a reservation notice or an ordinary Studio Board announcement.
   *
   * These nine sends used to carry NO authority at all, which put them on the
   * server's unscoped studio lane. That lane admits Directors, Managers,
   * Teachers and Front of House and refuses a Jr Staff ('ensemble') sender
   * outright, so a Jr Staff saved the record and nobody was told, on any
   * channel, with nothing logged. Naming the saved document here moves the
   * send onto the staff-scoped lane, where the server re-reads that document,
   * proves it names this caller, and admits only recipients the record itself
   * carries. See STAFF_SCOPED_SOURCES in
   * netlify/functions/lib/notificationAuthority.mjs.
   */
  staffScopedSource?: {
    kind: 'task' | 'rehearsalSchedule' | 'reservation' | 'studentMilestone' | 'discussion';
    id: string;
  };
}

/** One spelling per source kind, matching STAFF_SCOPED_SOURCES on the server. */
const STAFF_SCOPED_AUTHORITY_KEYS: Record<
  NonNullable<NotifyOptions['staffScopedSource']>['kind'],
  string
> = {
  task: 'staffTaskId',
  rehearsalSchedule: 'rehearsalScheduleId',
  reservation: 'staffReservationId',
  studentMilestone: 'studentMilestoneId',
  discussion: 'staffDiscussionId',
};

/**
 * A send the server REFUSED. Thrown only for a staff-scoped send, so the
 * producer's own catch fires instead of a refusal returning zeros that read
 * exactly like a quiet success.
 *
 * Deliberately NOT thrown for every lane. Thirty-odd other call sites treat a
 * returned result as the whole answer, several of them inside flows that tell
 * the user their message failed, and turning a refusal into a throw there
 * would be a user-facing change nobody asked for. The nine staff-scoped
 * producers all already carry a catch that logs and swallows, which is
 * precisely the catch the 2026-09-17 audit found never firing.
 */
export class NotificationRefusedError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`The server refused this notification (${status}). Nothing was delivered.`);
    this.name = 'NotificationRefusedError';
    this.status = status;
  }
}


export interface NotifyDeliveryResult {
  inApp: number;
  email: number;
  sms: number;
  push: number;
  skipped: number;
  errors: number;
  serverReached: boolean;
  /**
   * The source-backed outbox accepted responsibility for this send. Older
   * immediate delivery responses do not carry this field, so callers that do
   * not need durable outbox confirmation keep their existing serverReached
   * contract. A saved source must require this explicit acknowledgement
   * before saying its alerts are no longer pending.
   */
  queued?: boolean;
  /**
   * Whether this browser retained a local, source-only Retry alerts record.
   * False is an honest persistence-cap/storage warning: the source is saved,
   * but this device cannot recover a later failed request after navigation.
   */
  recoveryStored?: boolean;
  /**
   * The HTTP status of a refusal, when the server answered and said no. Zero
   * rows were written and no channel was attempted. Absent on every ordinary
   * send, including one that simply reached nobody.
   */
  refusedStatus?: number;
  /**
   * The server's own error text for a refusal, when it sent one. Read by the
   * closed-day notice (closedDayCancel.ts), which answers "this part is full"
   * by starting the next part instead of giving up.
   */
  refusedReason?: string;
  /**
   * Batched class cancellation only (PR #848 final review, finding 1). The
   * server wrote what it could, but some email, text or push never reached
   * its provider before the request's budget ran out. Those people's claims
   * were left free and the classes were NOT marked sent, so sending the
   * same part again reaches exactly them. Never read as "sent".
   */
  deliveryIncomplete?: boolean;
  /**
   * Batched class cancellation only (finding 3). Marker ids this part had no
   * room for. Nothing was written for them; they go to the next part.
   */
  deferredSourceIds?: string[];
  /**
   * Batched class cancellation only. Marker ids of a class that alone reaches
   * more people than one alert can carry. Nothing was written for them.
   */
  oversizeSourceIds?: string[];
  /**
   * Batched class cancellation only. How many people this part still owes,
   * after this request. The client keeps sending the part while it falls.
   */
  remainingRecipients?: number;
}
const NOTIFY_FN = '/.netlify/functions/notify';
const NOTIFY_REQUEST_TIMEOUT_MS = 45_000;

function pendingBoardSource(
  uid: string,
  authority: {
    bagGroupId?: string; bagMessageId?: string;
    compBoardPostId?: string; compBoardReplyId?: string;
    parentBoardThreadId?: string; parentBoardReplyId?: string;
    jrStaffBoardThreadId?: string; jrStaffBoardReplyId?: string;
    compParentsBoardPostId?: string; compParentsBoardReplyId?: string;
  }
): PendingBoardNotification | null {
  const source = (lane: PendingBoardNotificationLane, primaryId: string | undefined, secondaryId?: string): PendingBoardNotification | null =>
    primaryId ? { uid, lane, primaryId, secondaryId: secondaryId ?? '', createdAt: Date.now() } : null;
  if (authority.bagGroupId && authority.bagMessageId) return source('bagGroup', authority.bagGroupId, authority.bagMessageId);
  if (authority.compBoardPostId) return source('compBoard', authority.compBoardPostId, authority.compBoardReplyId);
  if (authority.parentBoardThreadId) return source('parentBoard', authority.parentBoardThreadId, authority.parentBoardReplyId);
  if (authority.jrStaffBoardThreadId) return source('jrStaffBoard', authority.jrStaffBoardThreadId, authority.jrStaffBoardReplyId);
  if (authority.compParentsBoardPostId) return source('compParentsBoard', authority.compParentsBoardPostId, authority.compParentsBoardReplyId);
  return null;
}

/** A 200 response is not a delivery claim. Saved board sources settle only after durable acceptance. */
export function notificationDurablyAccepted(result: NotifyDeliveryResult | null): boolean {
  return result?.serverReached === true && result.queued === true;
}

export const BOARD_NOTIFICATION_RECOVERY_UNAVAILABLE_EVENT = 'ldg:board-alert-recovery-unavailable';

export interface BoardNotificationRecoveryUnavailable {
  uid: string;
  lane: PendingBoardNotificationLane;
  primaryId: string;
  secondaryId: string;
}

function announceRecoveryUnavailable(source: PendingBoardNotification): void {
  try {
    if (typeof globalThis.CustomEvent !== 'function' || typeof globalThis.dispatchEvent !== 'function') return;
    globalThis.dispatchEvent(new CustomEvent<BoardNotificationRecoveryUnavailable>(
      BOARD_NOTIFICATION_RECOVERY_UNAVAILABLE_EVENT,
      { detail: source }
    ));
  } catch {
    // The caller still receives recoveryStored: false and can show its own warning.
  }
}

/** Retry one exact, source-only record. Its audience is always server-derived. */
export async function retryBoardNotificationSource(
  uid: string,
  row: Pick<PendingBoardNotification, 'lane' | 'primaryId' | 'secondaryId'>
): Promise<NotifyDeliveryResult | null> {
  const me = getAuth().currentUser;
  if (!me || me.uid !== uid) return null;
  const options: NotifyOptions = row.lane === 'bagGroup'
    ? { bagGroup: { id: row.primaryId, messageId: row.secondaryId }, forceRefreshToken: true }
    : row.lane === 'compBoard'
      ? { compBoardPost: { id: row.primaryId, ...(row.secondaryId ? { replyId: row.secondaryId } : {}) }, forceRefreshToken: true }
      : row.lane === 'parentBoard'
        ? { parentBoard: { threadId: row.primaryId, ...(row.secondaryId ? { replyId: row.secondaryId } : {}) }, forceRefreshToken: true }
        : row.lane === 'jrStaffBoard'
          ? { jrStaffBoard: { threadId: row.primaryId, ...(row.secondaryId ? { replyId: row.secondaryId } : {}) }, forceRefreshToken: true }
          : { compParentsBoard: { postId: row.primaryId, ...(row.secondaryId ? { replyId: row.secondaryId } : {}) }, forceRefreshToken: true };
  return notifyUsers([], { kind: row.lane === 'bagGroup' ? 'bag-group' : 'board', title: 'New board update' }, options);
}

/** Server-supported source-backed multi-class notification operations. */
export type BroadcastNotificationType =
  | 'assistantClassPosts'
  | 'calendarImport'
  // Bobbie, 2026-09-01: "1 will do, same for teachers" - one notice per
  // person for a studio-wide class cancellation (closedDayCancel.ts),
  // instead of one per class.
  | 'classCancellation';

/**
 * Stable identity for a logical operation, including across a full UI retry.
 * Callers provide the actor and the complete reviewed plan as parts. Web
 * Crypto is available on every supported secure Hub origin; the fallback is
 * deterministic for local harnesses that do not install it.
 */
export async function stableNotificationBroadcastId(
  label: string,
  parts: readonly string[]
): Promise<string> {
  const prefix = label.replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 24) || 'broadcast';
  const source = JSON.stringify(parts);
  if (globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(source)
    );
    const hex = Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
    return `${prefix}-${hex.slice(0, 48)}`;
  }
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${prefix}-fallback-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

/**
 * Ask the server to derive one canonical recipient union from records that
 * were already saved. No recipient, title, body or link comes from the
 * browser, so a stable id alone is never notification authority.
 */
export async function notifyBroadcast(
  broadcastId: string,
  broadcastType: BroadcastNotificationType,
  sourceRecordIds: string[]
): Promise<NotifyDeliveryResult | null> {
  if (!db || !/^[A-Za-z0-9._:-]{1,96}$/.test(broadcastId)) return null;
  const me = getAuth().currentUser;
  if (!me) return null;
  const sources = Array.from(new Set(sourceRecordIds.filter(Boolean))).sort();
  if (sources.length === 0 || sources.length > 500) return null;
  const fallback: NotifyDeliveryResult = {
    inApp: 0,
    email: 0,
    sms: 0,
    push: 0,
    skipped: 0,
    errors: 0,
    serverReached: false,
  };
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), NOTIFY_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(NOTIFY_FN, {
      method: 'POST',
      headers: await functionHeaders(me),
      signal: controller.signal,
      body: JSON.stringify({
        action: 'notifyBroadcast',
        broadcastId,
        broadcastType,
        sourceRecordIds: sources,
      }),
    });
    if (!response.ok) {
      console.error(`notifyBroadcast: server notify returned ${response.status}`);
      let refusedReason: string | undefined;
      try {
        const refusal = (await response.json()) as { error?: unknown };
        if (typeof refusal?.error === 'string') refusedReason = refusal.error;
      } catch {
        // No JSON body: the status alone is the honest answer.
      }
      return { ...fallback, refusedStatus: response.status, refusedReason };
    }
    const data = (await response.json()) as {
      results?: Partial<Omit<NotifyDeliveryResult, 'serverReached' | 'queued'>>;
      queued?: boolean;
      deliveryIncomplete?: unknown;
      deferredSourceIds?: unknown;
      oversizeSourceIds?: unknown;
      remainingRecipients?: unknown;
    };
    return {
      ...fallback,
      serverReached: true,
      queued: data.queued === true,
      inApp: Number(data.results?.inApp ?? 0),
      email: Number(data.results?.email ?? 0),
      sms: Number(data.results?.sms ?? 0),
      push: Number(data.results?.push ?? 0),
      skipped: Number(data.results?.skipped ?? 0),
      errors: Number(data.results?.errors ?? 0),
      ...(data.deliveryIncomplete === true ? { deliveryIncomplete: true } : {}),
      ...(Array.isArray(data.deferredSourceIds)
        ? {
            deferredSourceIds: data.deferredSourceIds.filter(
              (id): id is string => typeof id === 'string' && sources.includes(id)
            ),
          }
        : {}),
      ...(Array.isArray(data.oversizeSourceIds)
        ? {
            oversizeSourceIds: data.oversizeSourceIds.filter(
              (id): id is string => typeof id === 'string' && sources.includes(id)
            ),
          }
        : {}),
      ...(typeof data.remainingRecipients === 'number' && Number.isFinite(data.remainingRecipients)
        ? { remainingRecipients: data.remainingRecipients }
        : {}),
    };
  } catch (error) {
    console.error('notifyBroadcast: server notify unreachable', error);
    return fallback;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

/** Map a notification kind to an <Icon> name. */
export function iconForKind(kind: NotifKind): string {
  switch (kind) {
    case 'message':
      return 'chat-bubble';
    case 'mention':
      return 'bell';
    case 'task':
      return 'clipboard';
    case 'bulletin':
      return 'megaphone';
    case 'newsletter':
      return 'newspaper';
    case 'board':
      return 'thought-bubble';
    case 'bag-group':
      return 'people';
    case 'sub':
      return 'sos';
    case 'calendar':
      return 'calendar';
    case 'comp':
      return 'bell';
    case 'attendance':
      return 'warning';
    default:
      return 'bell';
  }
}

export interface UseMyNotificationsResult {
  notifications: NotificationDoc[];
  unread: number;
  loading: boolean;
  error: string | null;
  /** True until an opted-in notifications listener is server-confirmed. */
  fromCache: boolean;
  /**
   * Dismissed docs from the SAME query/snapshot, present only when the
   * caller passes `includeDismissed` - every other caller (the bell,
   * AppBadgeSync, headsUpData, /notifications, both command centers) never
   * asks for this and keeps its exact existing `notifications` contract
   * (undismissed only, unchanged). Bounded the same way `notifications`
   * already is - whatever falls inside `historyLimit`'s newest-first
   * window, no extra reads. Messages.tsx uses this so a generic notif card
   * a viewer already read and closed can route into Past instead of
   * vanishing outright (Bobbie, live, 2026-08-19: "NO it is not moving the
   * card to past after I click on it and read it! it just stays there",
   * "they just get stuck and I can't remove them!" - the query itself
   * already receives dismissed docs, `visibleNotificationWindow` was just
   * stripping them before any consumer ever saw them).
   */
  dismissed: NotificationDoc[];
}

/**
 * Subscribe to my most recent notifications, newest first. Set
 * `trackFromCache` when an empty list would produce an all-clear claim.
 * Set `includeDismissed` to also receive the `dismissed` field above - see
 * its own doc for why this is opt-in and additive, never a change to what
 * `notifications` itself returns.
 */
export function useMyNotifications(
  max = 40,
  trackFromCache = false,
  includeDismissed = false
): UseMyNotificationsResult {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationDoc[]>([]);
  const [dismissed, setDismissed] = useState<NotificationDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);
  const [historyLimit, setHistoryLimit] = useState(Math.max(1, max));

  useEffect(() => {
    setHistoryLimit(Math.max(1, max));
  }, [max, user?.uid]);

  useEffect(() => {
    const uid = user?.uid;
    if (!db || !uid) {
      setNotifications([]);
      setDismissed([]);
      setLoading(false);
      setFromCache(true);
      return;
    }
    const q = query(
      collection(db, 'notifications'),
      where('uid', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(historyLimit)
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: trackFromCache },
      (snap) => {
        const rows = snap.docs.map((d) => presentNotification(
          d.id,
          d.data() as Omit<NotificationDoc, 'id' | 'sourceIdentity'>
        ));
        const visible = visibleNotificationWindow(rows, max);
        const nextLimit = nextNotificationHistoryLimit(
          rows.length,
          visible.length,
          historyLimit,
          max
        );
        if (nextLimit !== null) {
          setHistoryLimit(nextLimit);
          return;
        }
        setNotifications(visible);
        // Same rows the query already fetched - the dismissed clause never
        // existed, visibleNotificationWindow just filtered client-side.
        // Zero extra reads, and every caller that leaves includeDismissed
        // false pays nothing extra (this state simply never gets read).
        if (includeDismissed) {
          setDismissed(rows.filter((n) => n.dismissed === true));
        }
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setFromCache(true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.uid, max, historyLimit, trackFromCache, includeDismissed]);

  const unread = notifications.reduce((n, x) => n + (x.read ? 0 : 1), 0);
  return { notifications, dismissed, unread, loading, error, fromCache };
}

export async function markNotificationRead(id: string): Promise<void> {
  if (!db) return;
  await updateDoc(doc(db, 'notifications', id), { read: true, readAt: serverTimestamp() });
}

/**
 * Bobbie, 2026-08-13: "it definitely should move to past messages unless I
 * mark as unread, which isn't even an option, which would be good too."
 *
 * The exact inverse of markNotificationRead, plus a server-time stamp. The
 * stamp is what lets the card SURVIVE: notificationReconcile re-marks a card
 * read whenever the linked source says this person already read through it,
 * and without a mark newer than that state the card would flip straight back.
 * The rules pin this write to exactly these fields on the recipient's own
 * non-dismissed card, so the archive stays one-way.
 */
export async function markNotificationUnread(id: string): Promise<void> {
  if (!db) return;
  await updateDoc(doc(db, 'notifications', id), {
    read: false,
    readAt: deleteField(),
    manuallyUnreadAt: serverTimestamp(),
  });
}

export async function markAllNotificationsRead(ids: string[]): Promise<void> {
  if (!db || ids.length === 0) return;
  const batch = writeBatch(db);
  for (const id of ids) {
    batch.update(doc(db, 'notifications', id), { read: true, readAt: serverTimestamp() });
  }
  await batch.commit();
}

export async function dismissNotification(notification: NotificationDoc): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const firestore = db;
  const expected = notification.sourceIdentity
    ?? captureNotificationIdentity(notification.id, notification);
  await runTransaction(firestore, async (tx) => {
    const ref = doc(firestore, 'notifications', notification.id);
    const current = await tx.get(ref);
    if (!current.exists()
        || current.data().uid !== me.uid
        || !notificationStillMatchesCapture(expected, current.data())) {
      throw new Error('That notification changed. Refresh and try again.');
    }
    if (current.data().dismissed === true) return;
    tx.update(ref, {
      dismissed: true,
      dismissedAt: serverTimestamp(),
      read: true,
      readAt: serverTimestamp(),
    });
  });
}

const NOTIFICATION_MUTATION_BATCH_SIZE = 450;

async function captureMyNotificationsWhere(
  predicate: (notification: CompletionNotificationRef, id: string) => boolean
): Promise<CapturedNotificationIdentity[]> {
  if (!db) throw new Error('Firestore not initialized.');
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const snapshot = await getDocs(query(
    collection(db, 'notifications'),
    where('uid', '==', me.uid)
  ));
  return snapshot.docs
    .filter((notification) => predicate(notification.data(), notification.id))
    .map((notification) => captureNotificationIdentity(notification.id, notification.data()));
}

async function mutateCapturedNotifications(
  captured: CapturedNotificationIdentity[],
  mode: 'dismiss' | 'read'
): Promise<number> {
  if (!db) throw new Error('Firestore not initialized.');
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const firestore = db;
  const unique = [...new Map(captured.map((item) => [item.id, item])).values()];
  let changed = 0;
  for (let offset = 0; offset < unique.length; offset += NOTIFICATION_MUTATION_BATCH_SIZE) {
    const chunk = unique.slice(offset, offset + NOTIFICATION_MUTATION_BATCH_SIZE);
    const changedInChunk = await runTransaction(firestore, async (tx) => {
      const current = await Promise.all(
        chunk.map((item) => tx.get(doc(firestore, 'notifications', item.id)))
      );
      let transactionChanges = 0;
      current.forEach((notification, index) => {
        const expected = chunk[index];
        if (!notification.exists()
            || notification.data().uid !== me.uid
            || !notificationStillMatchesCapture(expected, notification.data())) {
          return;
        }
        if (mode === 'dismiss') {
          if (notification.data().dismissed === true) return;
          tx.update(notification.ref, {
            dismissed: true,
            dismissedAt: serverTimestamp(),
            read: true,
            readAt: serverTimestamp(),
          });
        }
        else if (notification.data().read !== true) {
          tx.update(notification.ref, { read: true, readAt: serverTimestamp() });
        } else {
          return;
        }
        transactionChanges += 1;
      });
      return transactionChanges;
    });
    changed += changedInChunk;
  }
  return changed;
}

/** Capture the exact old cards before committing a thread inbox cursor. */
export async function captureNotificationsForThread(
  threadId: string
): Promise<CapturedNotificationIdentity[]> {
  return captureMyNotificationsWhere((notification) =>
    notificationMatchesThread(notification, threadId)
  );
}

export async function captureNotificationsForClassPosts(
  classPostIds: string[]
): Promise<CapturedNotificationIdentity[]> {
  const ids = new Set(classPostIds.filter(Boolean));
  return captureMyNotificationsWhere((notification) =>
    [...ids].some((classPostId) => notificationMatchesClassPost(notification, classPostId))
  );
}

/**
 * Capture every old "New activity" card for one Intern/executive-board
 * discussion (board.ts) before acknowledging, dismissing, or otherwise
 * handling it. Unlike thread/classPost, a discussion had NO clearing path
 * at all before this: acking it (Understood/complete) only ever touched the
 * discussion doc's own acks map, never the separate notifications/{id}
 * cards notify.mjs mints on every reply, so a fully-acknowledged board post
 * kept its old "New activity" alert sitting unread forever (Bobbie,
 * 2026-08-18: "I have acknowledged this twice in fact and it keeps showing
 * up!"). See notificationMatchesDiscussion for why this clears every reply's
 * card for the discussion at once, not just the most recent one.
 */
export async function captureNotificationsForDiscussion(
  discussionId: string
): Promise<CapturedNotificationIdentity[]> {
  if (!discussionId) return [];
  return captureMyNotificationsWhere((notification) =>
    notificationMatchesDiscussion(notification, discussionId)
  );
}

/**
 * Capture every card for one studio announcement before acknowledging,
 * dismissing or filing it (live report, 2026-09-24: those actions wrote only
 * the announcement, so its card stayed New on the bell forever). See
 * notificationMatchesBulletin for how a card from before the bulletinId stamp
 * is matched.
 */
export async function captureNotificationsForBulletin(
  bulletin: BulletinCardRef
): Promise<CapturedNotificationIdentity[]> {
  if (!bulletin.id) return [];
  return captureMyNotificationsWhere((notification) =>
    notificationMatchesBulletin(notification, bulletin)
  );
}

/**
 * Opening a post reads its cards. Asks the server for just this viewer's cards
 * for this one post (uid AND compBoardPostId equal, two single-field filters,
 * no composite index), rather than every card she holds like Done does.
 */
export async function captureNotificationsForCompBoardPostByKey(postId: string): Promise<CapturedNotificationIdentity[]> {
  if (!postId) return [];
  if (!db) throw new Error('Firestore not initialized.');
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const snapshot = await getDocs(query(
    collection(db, 'notifications'),
    where('uid', '==', me.uid),
    where('compBoardPostId', '==', postId)
  ));
  return snapshot.docs
    .filter((notification) => notificationMatchesCompBoardPost(notification.data(), postId))
    .map((notification) => captureNotificationIdentity(notification.id, notification.data()));
}

export async function captureNotificationsForCompBoardPost(postId: string): Promise<CapturedNotificationIdentity[]> {
  if (!postId) return [];
  return captureMyNotificationsWhere(notification => notificationMatchesCompBoardPost(notification, postId));
}

/**
 * Capture every card behind one comp board row in Messages: the same post, the
 * same state as the row (open, or filed in Past), minted no later than the
 * newest card the row showed (notificationInCompBoardRow). Not just the cards
 * that were on screen: Messages loads only the newest 40, so older cards for
 * the same post would slide into the freed slots as the same row again
 * (refute round 2). Not the whole post either: a reply minted after the row
 * was drawn, and an open reply in Main when the row is a filed Past row, are
 * not hers to lose (refute round 1). Read fresh from the server, so the
 * identity compared at clear time is the stored card. `excludeId` is the card
 * she tapped, which its own dismiss already handles.
 */
export async function captureNotificationsForCompBoardRow(
  postId: string,
  filed: boolean,
  drawnCards: readonly (CompletionNotificationRef & { id: string })[],
  excludeId?: string
): Promise<CapturedNotificationIdentity[]> {
  if (!postId) return [];
  const row: CompBoardRowBound = {
    postId,
    filed,
    throughMs: compBoardRowThroughMs(drawnCards),
    drawnIds: new Set(drawnCards.map((card) => card.id)),
  };
  return captureMyNotificationsWhere((notification, id) =>
    id !== excludeId && notificationInCompBoardRow(notification, id, row)
  );
}

/**
 * Capture every old card for one parent coordination room (bagGroups.ts)
 * before marking it handled. Mirrors captureNotificationsForDiscussion:
 * leaving bagMessageId off clears every message's card for the group at
 * once, since opening the group is what "acting on the source" means here.
 * Bag group posts have carried a heart and "seen" since BG-5 (the reaction
 * lane, netlify/functions/bagMessageReaction.mjs), but tapping one is not
 * what clears these cards: opening the group still is.
 */
export async function captureNotificationsForBagGroup(
  bagGroupId: string,
  bagMessageId?: string
): Promise<CapturedNotificationIdentity[]> {
  if (!bagGroupId) return [];
  return captureMyNotificationsWhere((notification) =>
    notificationMatchesBagGroup(notification, bagGroupId, bagMessageId)
  );
}

/**
 * Capture every old card for one sub request (subs.ts) before the claimer's
 * action clears it. Claiming a sub request is the "acting on the source"
 * moment for the person who saw the request and covered it.
 */
export async function captureNotificationsForSubRequest(
  subRequestId: string
): Promise<CapturedNotificationIdentity[]> {
  if (!subRequestId) return [];
  return captureMyNotificationsWhere((notification) =>
    notificationMatchesSubRequest(notification, subRequestId)
  );
}

/**
 * Capture every production-update card that says the SAME THING about the
 * same class, so filing one files the identical-looking pile behind it.
 *
 * Bobbie, live, 2026-09-04: "I have marked the song name as done with this
 * several times but it does not go away." Each song name entered for a class
 * mints its OWN card (the id hashes the song name) under a title that is the
 * same every time, so filing one left an indistinguishable one in place.
 *
 * `rawTitle` must be the STORED title, not the rendered one:
 * captureMyNotificationsWhere reads raw Firestore data, while the card on
 * screen has already been through recitalCompTrackerCopy. Callers pass
 * notification.sourceIdentity.title, which notificationPresentation.ts froze
 * from the raw record for exactly this reason.
 */
export async function captureNotificationsForProductionUpdate(
  productionOfferingId: string,
  rawTitle: string
): Promise<CapturedNotificationIdentity[]> {
  if (!productionOfferingId || !rawTitle) return [];
  return captureMyNotificationsWhere((notification) =>
    notificationMatchesProductionUpdate(notification, productionOfferingId, rawTitle)
  );
}

/**
 * Capture this account's Big/Little alert cards for one group board, plus the
 * Monday question card (DECISIONS 130), so opening the board can mark them
 * read the way opening a bag group does. See lib/bigLittleAlerts.ts.
 */
export async function captureNotificationsForBigLittleGroup(
  groupId: string
): Promise<CapturedNotificationIdentity[]> {
  if (!groupId) return [];
  return captureMyNotificationsWhere((notification) =>
    notificationMatchesBigLittleGroup(notification, groupId)
  );
}

export async function clearCapturedNotifications(
  captured: CapturedNotificationIdentity[]
): Promise<number> {
  return mutateCapturedNotifications(captured, 'dismiss');
}

export async function markCapturedNotificationsRead(
  captured: CapturedNotificationIdentity[]
): Promise<number> {
  return mutateCapturedNotifications(captured, 'read');
}

/**
 * Archive matching cards belonging only to the currently authenticated user.
 * The uid equality is in the Firestore query itself because notification list
 * rules require that constraint. Chunking stays below Firestore's 500-write
 * batch ceiling, so old accounts with a large history are cleaned fully.
 */
async function clearMyNotificationsWhere(
  predicate: (notification: CompletionNotificationRef) => boolean
): Promise<number> {
  if (!db) throw new Error('Firestore not initialized.');
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const firestore = db;
  const snapshot = await getDocs(query(
    collection(firestore, 'notifications'),
    where('uid', '==', me.uid)
  ));
  const matches = snapshot.docs.filter((notification) => predicate(notification.data()));

  for (let offset = 0; offset < matches.length; offset += NOTIFICATION_MUTATION_BATCH_SIZE) {
    const refs = matches.slice(offset, offset + NOTIFICATION_MUTATION_BATCH_SIZE).map((item) => item.ref);
    await runTransaction(firestore, async (tx) => {
      const current = await Promise.all(refs.map((ref) => tx.get(ref)));
      for (const notification of current) {
        if (notification.exists()
            && notification.data().dismissed !== true
            && predicate(notification.data())) {
          tx.update(notification.ref, {
            dismissed: true,
            dismissedAt: serverTimestamp(),
            read: true,
            readAt: serverTimestamp(),
          });
        }
      }
    });
  }
  return matches.length;
}

async function markMyNotificationsReadWhere(
  predicate: (notification: CompletionNotificationRef) => boolean
): Promise<number> {
  if (!db) throw new Error('Firestore not initialized.');
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const firestore = db;
  const snapshot = await getDocs(query(
    collection(firestore, 'notifications'),
    where('uid', '==', me.uid)
  ));
  const matches = snapshot.docs.filter((notification) =>
    notification.data().read !== true && predicate(notification.data())
  );
  for (let offset = 0; offset < matches.length; offset += NOTIFICATION_MUTATION_BATCH_SIZE) {
    const refs = matches.slice(offset, offset + NOTIFICATION_MUTATION_BATCH_SIZE).map((item) => item.ref);
    await runTransaction(firestore, async (tx) => {
      const current = await Promise.all(refs.map((ref) => tx.get(ref)));
      for (const notification of current) {
        if (notification.exists()
            && notification.data().read !== true
            && predicate(notification.data())) {
          tx.update(notification.ref, { read: true, readAt: serverTimestamp() });
        }
      }
    });
  }
  return matches.length;
}

/** Clear every old card for one ordinary or community conversation. */
export async function clearNotificationsForThread(threadId: string): Promise<number> {
  return clearMyNotificationsWhere((notification) =>
    notificationMatchesThread(notification, threadId)
  );
}

export async function markNotificationsReadForThread(threadId: string): Promise<number> {
  return markMyNotificationsReadWhere((notification) =>
    notificationMatchesThread(notification, threadId)
  );
}

export async function clearNotificationsForClassPosts(classPostIds: string[]): Promise<number> {
  const ids = new Set(classPostIds.filter(Boolean));
  return clearMyNotificationsWhere((notification) =>
    [...ids].some((classPostId) => notificationMatchesClassPost(notification, classPostId))
  );
}

export async function markNotificationsReadForClassPost(classPostId: string): Promise<number> {
  return markMyNotificationsReadWhere((notification) =>
    notificationMatchesClassPost(notification, classPostId)
  );
}

/** Clear every old card for one completed task. */
export async function clearNotificationsForTask(taskId: string): Promise<number> {
  return clearMyNotificationsWhere((notification) =>
    notificationMatchesTask(notification, taskId)
  );
}

/**
 * Notify a set of recipients across every channel through the authenticated
 * Function. Excludes the sender. Safe to call from any active user's flow.
 */
export async function notifyUsers(
  recipientUids: string[],
  payload: NotifyPayload,
  options: NotifyOptions = {}
): Promise<NotifyDeliveryResult | null> {
  if (!db) return null;
  const me = getAuth().currentUser;
  if (!me) return null;
  // ONE HUMAN, ONE EXCLUSION - and the SERVER is where that is decided. A Jr
  // Staff holding both her dancer sign-in and her email account must not hear
  // her own words on her other phone (Bobbie, 2026-09-07), but a browser
  // cannot prove which uids are hers: a plain staff account's useUsersList is
  // active staff only, and a family caller holds nothing but her own doc.
  // notify.mjs resolves her whole sign-in set from users/{uid}.studentId with
  // the Admin SDK and applies it at the one off-app choke point, so this line
  // stays a cheap pre-filter on the uid actually signed in rather than a
  // second, weaker answer to the same question.
  const recipients = Array.from(new Set(recipientUids)).filter((uid) => uid && uid !== me.uid);
  // A fresh comp board post (never a reply) is the one case where an empty
  // client-side seed must NOT stop the request: the server replaces this
  // list entirely with its own roll-union-staff read (see the 'compBoard'
  // authority lane), and a family caller structurally cannot resolve "every
  // active staff account" itself to seed a non-empty list in the first
  // place. Bailing here on "nobody to tell" would silently mean nobody -
  // including every active staff account the server could still reach - is
  // ever notified about that post. Every other authority keeps the ordinary
  // "nothing to send" short circuit.
  const isFreshCompBoardPost = Boolean(options.compBoardPost?.id.trim()) && !options.compBoardPost?.replyId;
  const isCompBoardSource = Boolean(options.compBoardPost?.id.trim());
  // A parent tip is the same case and then some: its family caller sends NO
  // recipients at all, because it cannot resolve the roll. Bailing here on
  // "nobody to tell" would mean the server never runs its own derivation and
  // the tip silently notifies nobody - which is exactly the bug being fixed.
  const isCompTip = Boolean(options.compTip?.id.trim());
  // Same as a tip: a parent board client sends no recipients, because the
  // server works out the board (or the conversation) from its own reads.
  const isParentBoard = Boolean(options.parentBoard?.threadId.trim());
  // Same again for the comp parents board: its member list is server-owned,
  // so the caller sends nothing and the server derives everyone.
  const isCompParentsBoard = Boolean(options.compParentsBoard?.postId.trim());
  // Same again for the Jr Staff board: its client sends no recipients
  // because a family member cannot resolve the roll and even a staff
  // composer cannot resolve the guardian half; the server derives both.
  const isJrStaffBoard = Boolean(options.jrStaffBoard?.threadId.trim());
  // A Bag Group retry carries only the already-saved source ids. Its server
  // lane re-derives the current group audience, so a local recovery record
  // never keeps or replays a stale recipient list.
  const isBagGroupSource = Boolean(options.bagGroup?.id.trim() && options.bagGroup?.messageId.trim());
  if (recipients.length === 0
    && !isFreshCompBoardPost && !isCompTip && !isParentBoard && !isJrStaffBoard
    && !isCompParentsBoard && !isBagGroupSource && !isCompBoardSource) return null;

  const requestedCommunityThreadId = options.communityThread?.id.trim() ?? '';
  const requestedCommunitySenderName = options.communityThread?.senderName.trim() ?? '';
  const hasCommunityThread = Boolean(requestedCommunityThreadId && requestedCommunitySenderName);
  if (Boolean(requestedCommunityThreadId) !== Boolean(requestedCommunitySenderName)) {
    console.error('notifyUsers: incomplete community notification authority');
    return null;
  }
  const requestedStaffScopedKind = options.staffScopedSource?.kind ?? null;
  const requestedStaffScopedId = options.staffScopedSource?.id.trim() ?? '';
  const requestedStaffScopedKey = requestedStaffScopedKind
    ? STAFF_SCOPED_AUTHORITY_KEYS[requestedStaffScopedKind]
    : '';
  const requestedThreadId = options.thread?.id.trim() ?? '';
  const requestedBagGroupId = options.bagGroup?.id.trim() ?? '';
  const requestedBagMessageId = options.bagGroup?.messageId.trim() ?? '';
  const requestedClassPostId = options.classPost?.id.trim() ?? '';
  const requestedClassReplyId = options.classPost?.replyId?.trim() ?? '';
  const requestedCompTipId = options.compTip?.id.trim() ?? '';
  const requestedParentBoardThreadId = options.parentBoard?.threadId.trim() ?? '';
  const requestedParentBoardReplyId = options.parentBoard?.replyId?.trim() ?? '';
  const requestedCompParentsBoardPostId = options.compParentsBoard?.postId.trim() ?? '';
  const requestedCompParentsBoardReplyId = options.compParentsBoard?.replyId?.trim() ?? '';
  const requestedJrStaffBoardThreadId = options.jrStaffBoard?.threadId.trim() ?? '';
  const requestedJrStaffBoardReplyId = options.jrStaffBoard?.replyId?.trim() ?? '';
  const requestedJrStaffBoardMirrorOfThreadId = options.jrStaffBoard?.mirrorOfThreadId?.trim() ?? '';
  const requestedJrStaffBoardMirrorOfMessageId = options.jrStaffBoard?.mirrorOfMessageId?.trim() ?? '';
  const requestedCompBoardPostId = options.compBoardPost?.id.trim() ?? '';
  const requestedCompBoardReplyId = options.compBoardPost?.replyId?.trim() ?? '';
  const requestedCompBoardCountdownId = options.compBoardCountdown?.id.trim() ?? '';
  const requestedSubRequestId = options.subRequest?.id.trim() ?? '';
  const requestedSubStudentIds = options.subRequest?.recipientStudentIds ?? {};
  // Not a lane selector - see the field's own doc comment on NotifyOptions.
  // Deliberately excluded from authorityCount below: it coexists with the
  // plain unscoped 'mention' send it accompanies rather than competing with
  // it for the one-authority-per-send rule that field enforces.
  const requestedDiscussionMentionId = options.discussionMention?.discussionId.trim() ?? '';
  // Required alongside discussionId now (PR #807 round 8, Codex P1-b) - the
  // server derives the discussion from a verified reply doc at this exact
  // (discussionId, replyId) path, never from discussionId alone. See
  // NotifyOptions.discussionMention's own comment.
  const requestedDiscussionMentionReplyId = options.discussionMention?.replyId.trim() ?? '';
  const authorityCount = [
    hasCommunityThread,
    Boolean(requestedThreadId),
    Boolean(requestedBagGroupId && requestedBagMessageId),
    Boolean(requestedClassPostId),
    Boolean(requestedCompBoardPostId),
    Boolean(requestedCompBoardCountdownId),
    Boolean(requestedCompTipId),
    Boolean(requestedParentBoardThreadId),
    Boolean(requestedCompParentsBoardPostId),
    Boolean(requestedJrStaffBoardThreadId),
    Boolean(requestedSubRequestId),
  ].filter(Boolean).length;
  if (authorityCount > 1
    || Boolean(requestedBagGroupId) !== Boolean(requestedBagMessageId)
    || (Boolean(requestedClassReplyId) && !requestedClassPostId)
    || (Boolean(requestedCompBoardReplyId) && !requestedCompBoardPostId)
    || (Boolean(requestedParentBoardReplyId) && !requestedParentBoardThreadId)
    || (Boolean(requestedCompParentsBoardReplyId) && !requestedCompParentsBoardPostId)
    || (Boolean(requestedJrStaffBoardReplyId) && !requestedJrStaffBoardThreadId)
    || (Object.keys(requestedSubStudentIds).length > 0 && !requestedSubRequestId)
    || (Boolean(requestedDiscussionMentionId) !== Boolean(requestedDiscussionMentionReplyId))) {
    console.error('notifyUsers: invalid or conflicting notification authority');
    return null;
  }

  const fallback: NotifyDeliveryResult = {
    inApp: 0,
    email: 0,
    sms: 0,
    push: 0,
    skipped: 0,
    errors: 0,
    serverReached: false,
  };

  const notificationAuthority = {
    ...(requestedStaffScopedKey && requestedStaffScopedId
      ? { [requestedStaffScopedKey]: requestedStaffScopedId }
      : {}),
    ...(hasCommunityThread ? { communityThreadId: requestedCommunityThreadId } : {}),
    ...(requestedThreadId ? { threadId: requestedThreadId } : {}),
    ...(requestedBagGroupId
      ? { bagGroupId: requestedBagGroupId, bagMessageId: requestedBagMessageId }
      : {}),
    ...(requestedClassPostId
      ? {
          classPostId: requestedClassPostId,
          ...(requestedClassReplyId ? { classReplyId: requestedClassReplyId } : {}),
        }
      : {}),
    ...(requestedCompBoardPostId
      ? {
          compBoardPostId: requestedCompBoardPostId,
          ...(requestedCompBoardReplyId ? { compBoardReplyId: requestedCompBoardReplyId } : {}),
        }
      : {}),
    ...(requestedCompBoardCountdownId ? { compBoardCountdownId: requestedCompBoardCountdownId } : {}),
    ...(requestedCompTipId ? { compTipId: requestedCompTipId } : {}),
    ...(requestedParentBoardThreadId
      ? {
          parentBoardThreadId: requestedParentBoardThreadId,
          ...(requestedParentBoardReplyId
            ? { parentBoardReplyId: requestedParentBoardReplyId }
            : {}),
        }
      : {}),
    ...(requestedCompParentsBoardPostId
      ? {
          compParentsBoardPostId: requestedCompParentsBoardPostId,
          ...(requestedCompParentsBoardReplyId
            ? { compParentsBoardReplyId: requestedCompParentsBoardReplyId }
            : {}),
        }
      : {}),
    ...(requestedJrStaffBoardThreadId
      ? {
          jrStaffBoardThreadId: requestedJrStaffBoardThreadId,
          ...(requestedJrStaffBoardReplyId
            ? { jrStaffBoardReplyId: requestedJrStaffBoardReplyId }
            : {}),
          ...(requestedJrStaffBoardMirrorOfThreadId
            ? { jrStaffBoardMirrorOfThreadId: requestedJrStaffBoardMirrorOfThreadId }
            : {}),
          ...(requestedJrStaffBoardMirrorOfMessageId
            ? { jrStaffBoardMirrorOfMessageId: requestedJrStaffBoardMirrorOfMessageId }
            : {}),
        }
      : {}),
    ...(requestedSubRequestId
      ? {
          subRequestId: requestedSubRequestId,
          ...(Object.keys(requestedSubStudentIds).length > 0
            ? { recipientStudentIds: requestedSubStudentIds }
            : {}),
        }
      : {}),
    ...(requestedDiscussionMentionId
      ? {
          discussionMentionId: requestedDiscussionMentionId,
          discussionMentionReplyId: requestedDiscussionMentionReplyId,
        }
      : {}),
  };

  // `server: false` is an explicit caller choice for an in-app-only update,
  // not a failed phone-alert request. Do not manufacture a Retry alert for
  // that deliberate channel opt-out.
  const recoverableSource = options.server === false
    ? null
    : pendingBoardSource(me.uid, notificationAuthority);
  // Persist before HTTP. The record is intentionally source-only; the server
  // re-reads current membership and message ownership for every retry.
  const recoveryStored = recoverableSource
    ? rememberPendingBoardNotification(recoverableSource)
    : undefined;

  // In-app cards are now written by the authenticated Function. `server:
  // false` disables only email/SMS/push; source-backed family/community
  // cards still need the server authority boundary.
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => controller.abort(),
    NOTIFY_REQUEST_TIMEOUT_MS
  );
  try {
    const response = await fetch(NOTIFY_FN, {
      method: 'POST',
      headers: await functionHeaders(me, { forceRefresh: options.forceRefreshToken === true }),
      signal: controller.signal,
      body: JSON.stringify({
        recipientUids: recipients,
        ...payload,
        notificationAuthority,
        external: options.server !== false,
      }),
    });
    if (!response.ok) {
      // A 401 here is the stale-service-account signature; a 404 means the
      // function is not deployed. Both used to vanish into the fallback with
      // no trace on any device - name the status so a dead server channel is
      // diagnosable from a phone's console.
      console.error(`notifyUsers: server notify returned ${response.status}`);
      if (recoverableSource && recoveryStored === false) announceRecoveryUnavailable(recoverableSource);
      const refused = { ...fallback, refusedStatus: response.status };
      // A REFUSAL IS NOT A QUIET ZERO. On a staff-scoped send the server
      // answering no means nothing was written and no channel was attempted,
      // and the old return handed that back as a result indistinguishable
      // from "sent, reached nobody" - which is how a Jr Staff's to-do
      // assignment stayed silent for three weeks with a catch block sitting
      // right there that never fired (audit lane B, 2026-09-17).
      if (requestedStaffScopedKey && requestedStaffScopedId) {
        throw new NotificationRefusedError(response.status);
      }
      return recoverableSource ? { ...refused, recoveryStored } : refused;
    }
    const data = (await response.json()) as {
      results?: Partial<Omit<NotifyDeliveryResult, 'serverReached' | 'queued'>>;
      queued?: boolean;
    };
    if (data.queued === true && recoverableSource) clearPendingBoardNotification(recoverableSource);
    if (data.queued !== true && recoverableSource && recoveryStored === false) announceRecoveryUnavailable(recoverableSource);
    return {
      ...fallback,
      serverReached: true,
      queued: data.queued === true,
      ...(recoverableSource ? { recoveryStored } : {}),
      inApp: Number(data.results?.inApp ?? 0),
      email: Number(data.results?.email ?? 0),
      sms: Number(data.results?.sms ?? 0),
      push: Number(data.results?.push ?? 0),
      skipped: Number(data.results?.skipped ?? 0),
      errors: Number(data.results?.errors ?? 0),
    };
  } catch (e) {
    // The refusal above is raised from inside this same try, so it has to be
    // let past here or this catch would swallow the one thing it exists to
    // make loud.
    if (e instanceof NotificationRefusedError) throw e;
    console.error('notifyUsers: server notify unreachable', e);
    if (recoverableSource && recoveryStored === false) announceRecoveryUnavailable(recoverableSource);
    return recoverableSource ? { ...fallback, recoveryStored } : fallback;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}
/** Retry pending board sources only for the currently signed-in account. */
export async function retryPendingBoardNotifications(
  uid: string,
  lane: PendingBoardNotificationLane,
  primaryId?: string
): Promise<{ accepted: number; pending: number }> {
  const me = getAuth().currentUser;
  if (!me || me.uid !== uid) return { accepted: 0, pending: pendingBoardNotifications(uid, lane, primaryId).length };
  const rows = pendingBoardNotifications(uid, lane, primaryId);
  let accepted = 0;
  for (const row of rows) {
    const result = await retryBoardNotificationSource(uid, row);
    if (notificationDurablyAccepted(result)) accepted += 1;
  }
  return { accepted, pending: pendingBoardNotifications(uid, lane, primaryId).length };
}

/**
 * The pure half of notifyAudience: which uids out of a raw `users` read
 * belong to this audience. Pulled out so it is unit-testable with plain
 * objects, no Firestore mock required - see
 * tests/notify-audience-canary-exclusion.test.mjs.
 *
 * The video pipeline canary's fixture guardian (docs/video-robustness-plan-
 * 2026-08-18.md section 3) is role 'family' and status 'active' - exactly
 * the shape an "all active users" or "all family" broadcast audience would
 * otherwise sweep up, minting a real notification doc for a synthetic uid
 * on every such bulletin. Filtered here, the one place this audience gets
 * assembled.
 */
export function audienceUidsFromActiveUsers(
  users: Array<{ uid: string; role: UserRole; canary?: boolean }>,
  audienceRoles: UserRole[]
): string[] {
  return users
    .filter((u) => u.canary !== true)
    .filter((u) => audienceRoles.length === 0 || audienceRoles.includes(u.role))
    .map((u) => u.uid);
}

/**
 * Notify everyone whose role is in `audienceRoles` (empty = all active users).
 * Reads the users collection, so only a Director (who can list users) should
 * call this. Used for broadcasts like bulletins + newsletters.
 */
export async function notifyAudience(
  audienceRoles: UserRole[],
  payload: NotifyPayload,
  options: NotifyOptions = {}
): Promise<void> {
  if (!db) return;
  /* THE ROLE FILTER IS WHAT MAKES THIS QUERY PROVABLE, and without it a Jr
     Staff sender's announcement notified NOBODY.

     Rules are not filters. The users read rule gives a resource-independent
     grant only to the caller and to a Director; manager/foh/company are
     covered by a status disjunct; but an `ensemble` (Jr Staff) caller matches
     only the last disjunct, which also requires
     `isStaffRole(resource.data.role)`. A query filtered on status ALONE can
     return family documents, so that disjunct cannot be proven and Firestore
     denies the WHOLE LIST - the failure mode this repo has been bitten by six
     times, because it looks exactly like an empty result.

     board.ts then caught the denial and dropped it, so the composer cleared,
     the announcement appeared on the board, the review sheet had already shown
     her a headcount, and not one notification existed.

     Filtering by role changes no recipient: audienceUidsFromActiveUsers
     applies the same predicate to the result anyway. It only makes the query
     prove what the rule asks, and reads less. The bare status query stays for
     the empty-audience case, which is Director-only and needs the whole
     collection. */
  const roleScoped = audienceRoles.length > 0;
  const snap = await getDocs(roleScoped
    ? query(
      collection(db, 'users'),
      where('status', '==', 'active'),
      where('role', 'in', audienceRoles)
    )
    : query(collection(db, 'users'), where('status', '==', 'active')));
  const users = snap.docs.map((d) => d.data() as { uid: string; role: UserRole; canary?: boolean });
  const uids = audienceUidsFromActiveUsers(users, audienceRoles);
  await notifyUsers(uids, payload, options);
}

/**
 * Ask the server to notify the current private Intern audience from the
 * committed source record. The browser supplies no recipients or preview
 * copy, so a family-role Intern cannot turn this into a general broadcast.
 */
export async function notifyDiscussionEvent(
  discussionId: string,
  discussionReplyId?: string
): Promise<NotifyDeliveryResult | null> {
  const me = getAuth().currentUser;
  const sourceId = discussionId.trim();
  const replyId = discussionReplyId?.trim() ?? '';
  if (!me || !sourceId || sourceId.includes('/') || (replyId && replyId.includes('/'))) return null;

  const fallback: NotifyDeliveryResult = {
    inApp: 0,
    email: 0,
    sms: 0,
    push: 0,
    skipped: 0,
    errors: 0,
    serverReached: false,
  };
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => controller.abort(),
    NOTIFY_REQUEST_TIMEOUT_MS
  );
  try {
    const idToken = await me.getIdToken();
    const response = await fetch(NOTIFY_FN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      signal: controller.signal,
      body: JSON.stringify({
        action: 'notifyDiscussion',
        discussionId: sourceId,
        ...(replyId ? { discussionReplyId: replyId } : {}),
      }),
    });
    if (!response.ok) {
      console.error(`notifyDiscussionEvent: server notify returned ${response.status}`);
      return fallback;
    }
    const data = (await response.json()) as { results?: Partial<Omit<NotifyDeliveryResult, 'serverReached'>> };
    return {
      ...fallback,
      serverReached: true,
      inApp: Number(data.results?.inApp ?? 0),
      email: Number(data.results?.email ?? 0),
      sms: Number(data.results?.sms ?? 0),
      push: Number(data.results?.push ?? 0),
      skipped: Number(data.results?.skipped ?? 0),
      errors: Number(data.results?.errors ?? 0),
    };
  } catch (e) {
    console.error('notifyDiscussionEvent: server notify unreachable', e);
    return fallback;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

/**
 * The Recital/Comp Tracker music moments the Directors need to hear about.
 */
export type ProductionUpdateKind = 'final-cut' | 'music-upload' | 'song-name';

/**
 * Tell the server one of those things just landed on a class.
 *
 * The browser sends the class id and which of the two it was, and nothing
 * else: recipients, title, body and link are all derived server-side from the
 * offering document, which is also where the claim itself is checked. So this
 * carries no authority, and a failure here can never fail the save that came
 * before it. Best effort by design, exactly like the rest of the notification
 * fan-out: the write is already committed by the time this runs, and a person
 * must never see "could not save" because an alert did not go out.
 */
export async function notifyProductionUpdate(
  offeringId: string,
  update: ProductionUpdateKind,
  musicLinkId?: string
): Promise<void> {
  try {
    const sourceId = offeringId.trim();
    const linkId = musicLinkId?.trim() ?? '';
    if (!sourceId || sourceId.includes('/')) return;
    if (update === 'final-cut' || update === 'music-upload' ? !linkId : Boolean(linkId)) return;
    const me = getAuth().currentUser;
    if (!me) return;
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), NOTIFY_REQUEST_TIMEOUT_MS);
    try {
      const idToken = await me.getIdToken();
      const response = await fetch(NOTIFY_FN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        signal: controller.signal,
        body: JSON.stringify({
          action: 'notifyProductionUpdate',
          offeringId: sourceId,
          productionUpdate: update,
          ...(linkId ? { productionMusicLinkId: linkId } : {}),
        }),
      });
      if (!response.ok) {
        console.error(`notifyProductionUpdate: server notify returned ${response.status}`);
      }
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  } catch (e) {
    console.error('notifyProductionUpdate: server notify unreachable', e);
  }
}

/**
 * Send a test notification to MYSELF across every channel I have on. Writes an
 * in-app doc and asks the function to email/SMS me (the function honors a
 * `test` flag so it includes the caller, which the normal path excludes). Lets
 * Bobbie confirm each channel from her own phone.
 */
/**
 * Returns what the send actually did, instead of nothing.
 *
 * The response body has always carried `results`, and this function has always
 * thrown it away, so "Send a test" could only ever say "sent" - true even when
 * FCM rejected every token. Returning it lets the Account page tell the
 * difference between accepted-but-not-shown and rejected, which are the two
 * halves of the iPhone investigation. Still best-effort: null means we could
 * not find out, and the caller says so rather than inventing a result.
 */
export async function sendTestNotification(
  currentDeviceToken: string | null,
  testId: string,
  testDelayMs = 8_000,
  onRequestStarted?: () => void
): Promise<TestSendResults | null> {
  if (!db) return null;
  const me = getAuth().currentUser;
  if (!me) return null;
  try {
    const idToken = await me.getIdToken();
    const request = fetch(NOTIFY_FN, {
      method: 'POST',
      // Keep the queued request alive while the Home Screen app moves to the
      // background so the delayed test can exercise the real system-banner
      // path instead of the foreground onMessage path.
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({
        test: true,
        // Keep this property even when null. The server then reports that
        // THIS browser is not enrolled instead of testing another device on
        // the account and giving a false green result on the phone in hand.
        testDeviceToken: currentDeviceToken,
        testId,
        testDelayMs,
        recipientUids: [me.uid],
        kind: 'general',
        title: 'Test notification from the Hub',
        body: 'Your email and/or text channel is working.',
        link: '/notifications',
        external: true,
      }),
    });
    // Do not tell someone to lock the phone until Auth is ready and the
    // keepalive request has actually been queued. Locking before getIdToken
    // finishes can suspend iOS before the server ever receives the test.
    onRequestStarted?.();
    const response = await request;
    if (!response.ok) return null;
    const payload = (await response.json().catch(() => null)) as
      | { results?: TestSendResults }
      | null;
    return payload?.results ?? null;
  } catch {
    return null;
  }
}
